import React, { useState, useEffect, useMemo } from 'react';
import './CategoryForm.css';
import { createSlug } from '../../utils/categoryUtils';
import { getApiBaseUrlWithoutPath } from '../../config/environment';

/** Backend / hook may use `parent` or `parentCategory` (object or id string). */
function getCategoryParentRefId(category) {
  if (!category || typeof category !== 'object') return '';
  const p = category.parentCategory ?? category.parent;
  if (p == null || p === '') return '';
  if (typeof p === 'object') return String(p._id ?? p.id ?? '').trim();
  return String(p).trim();
}

/** Depth in admin tree: 0 = main, 1 = sub, 2 = child (matches API `level`). */
function getCategoryDepthInAdminTree(tree, targetId) {
  if (targetId == null || targetId === '') return -1;
  const t = String(targetId);
  const walk = (nodes, depth) => {
    if (!Array.isArray(nodes)) return -1;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      const id = String(node._id ?? node.id ?? '');
      if (id === t) return depth;
      const sub = walk(node.children, depth + 1);
      if (sub >= 0) return sub;
    }
    return -1;
  };
  return walk(tree, 0);
}

function resolveCategoryDepth(category, categoriesTree) {
  if (!category) return 0;
  const tid = category._id ?? category.id;
  const d = getCategoryDepthInAdminTree(categoriesTree || [], tid);
  if (d >= 0) return d;
  const lv = Number(category.level);
  if (Number.isFinite(lv) && lv >= 0 && lv <= 2) return lv;
  const pid = getCategoryParentRefId(category);
  if (!pid) return 0;
  const parentDepth = getCategoryDepthInAdminTree(categoriesTree || [], pid);
  if (parentDepth >= 0) return parentDepth + 1;
  return 1;
}

/** Parent id on any category row (flat API shape). */
function getRowParentId(cat) {
  if (!cat || typeof cat !== 'object') return '';
  const p = cat.parentCategory ?? cat.parent;
  if (p == null || p === '') return '';
  if (typeof p === 'object') return String(p._id ?? p.id ?? '').trim();
  return String(p).trim();
}

/** Which root main owns this subcategory id (admin tree). */
function findMainIdForSubcategory(tree, subId) {
  if (subId == null || subId === '') return '';
  const s = String(subId);
  for (const main of tree || []) {
    if (!main?.children?.length) continue;
    if (main.children.some((sub) => String(sub._id ?? sub.id) === s)) {
      return String(main._id ?? main.id ?? '');
    }
  }
  return '';
}

/**
 * Derive API `parent` from the three dropdowns. Handles edit vs create (sub dropdown may hold "self" when editing a sub).
 */
function resolveParentIdForSubmit(editingId, selMain, selSub, selChild) {
  const eid = editingId != null && editingId !== '' ? String(editingId) : '';
  const mainS = selMain ? String(selMain) : '';
  const subS = selSub ? String(selSub) : '';
  const childS = selChild ? String(selChild) : '';

  if (eid && childS === eid) return subS || null;
  if (eid && subS === eid) return mainS || null;
  if (eid && mainS === eid && !subS) return null;
  if (eid && mainS && mainS !== eid && !subS && !childS) return mainS;

  if (subS) return subS;
  if (mainS) return mainS;
  return null;
}

const CategoryForm = ({ category, onSubmit, onCancel, categories }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent: '',
    image: '',
    isFeatured: false,
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = React.useRef(null);
  
  // 3-Level Category Structure State
  const [mainCategories, setMainCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [childrenCategories, setChildrenCategories] = useState([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedChildrenCategory, setSelectedChildrenCategory] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(false);

  const categoryDepth = useMemo(
    () => (category ? resolveCategoryDepth(category, categories) : null),
    [category, categories]
  );

  const mainOptions = useMemo(() => {
    const map = new Map();
    const add = (c) => {
      if (!c || typeof c !== 'object') return;
      const id = String(c._id ?? c.id ?? '').trim();
      if (!id) return;
      if (!map.has(id)) map.set(id, c);
    };
    (categories || []).forEach(add);
    (mainCategories || []).forEach(add);
    return Array.from(map.values());
  }, [categories, mainCategories]);

  const subOptions = useMemo(() => {
    const list = Array.isArray(subcategories) ? [...subcategories] : [];
    if (category && categoryDepth === 1) {
      const sid = String(category._id ?? category.id ?? '');
      if (sid && !list.some((s) => String(s._id ?? s.id) === sid)) {
        list.unshift(category);
      }
    }
    return list;
  }, [subcategories, category, categoryDepth]);

  const childOptions = useMemo(() => {
    const list = Array.isArray(childrenCategories) ? [...childrenCategories] : [];
    if (category && categoryDepth === 2) {
      const cid = String(category._id ?? category.id ?? '');
      if (cid && !list.some((c) => String(c._id ?? c.id) === cid)) {
        list.unshift(category);
      }
    }
    return list;
  }, [childrenCategories, category, categoryDepth]);

  // Fetch main categories on component mount
  useEffect(() => {
    fetchMainCategories();
  }, []);

  // Load category data and determine selected categories
  useEffect(() => {
    if (category) {
      const depth = resolveCategoryDepth(category, categories);
      const parentRefId = getCategoryParentRefId(category);

      setFormData({
        name: category.name || '',
        description: category.description || '',
        parent: parentRefId,
        image: category.image || '',
        isFeatured: category.isFeatured || false,
        isActive: category.isActive !== undefined ? category.isActive : true
      });
      setImagePreview(category.image || null);
      
      const selfId = String(category._id ?? category.id ?? '');

      if (depth === 2) {
        const parentSubcategoryId = String(parentRefId || '');
        const mainFromTree = findMainIdForSubcategory(categories, parentSubcategoryId);
        setSelectedMainCategory(mainFromTree);
        setSelectedSubcategory(parentSubcategoryId);
        setSelectedChildrenCategory(selfId);
        if (mainFromTree) {
          fetchSubcategories(mainFromTree);
        } else if (parentSubcategoryId) {
          fetchSubcategoriesForEdit(parentSubcategoryId);
        }
        if (parentSubcategoryId) {
          fetchChildrenCategories(parentSubcategoryId);
        }
      } else if (depth === 1) {
        const parentMainCategoryId = String(parentRefId || '');
        setSelectedMainCategory(parentMainCategoryId);
        setSelectedSubcategory(selfId);
        setSelectedChildrenCategory('');
        if (parentMainCategoryId) {
          fetchSubcategories(parentMainCategoryId);
        }
        if (selfId) {
          fetchChildrenCategories(selfId);
        }
      } else {
        setSelectedMainCategory(selfId);
        setSelectedSubcategory('');
        setSelectedChildrenCategory('');
        fetchSubcategories(selfId);
      }
    } else {
      setFormData({
        name: '',
        description: '',
        parent: '',
        image: '',
        isFeatured: false,
        isActive: true
      });
      setImagePreview(null);
      setSelectedMainCategory('');
      setSelectedSubcategory('');
      setSelectedChildrenCategory('');
      setSubcategories([]);
      setChildrenCategories([]);
    }
  }, [category, categories]);

  const handleMainCategoryChange = (e) => {
    const v = e.target.value;
    setSelectedMainCategory(v);
    setSelectedSubcategory('');
    setSelectedChildrenCategory('');
    if (v) {
      fetchSubcategories(v);
    } else {
      setSubcategories([]);
      setChildrenCategories([]);
    }
  };

  const handleSubcategoryChange = (e) => {
    const v = e.target.value;
    setSelectedSubcategory(v);
    setSelectedChildrenCategory('');
    if (v) {
      fetchChildrenCategories(v);
    } else {
      setChildrenCategories([]);
    }
  };

  const handleChildrenCategoryChange = (e) => {
    setSelectedChildrenCategory(e.target.value);
  };

  // Fetch main categories
  const fetchMainCategories = async () => {
    try {
      setLoadingCategories(true);
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      const apiUrl = getApiBaseUrlWithoutPath();
      
      // Try to fetch from main categories endpoint first
      try {
        const response = await fetch(`${apiUrl}/api/admin/categories/main`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setMainCategories(Array.isArray(data.data) ? data.data : []);
            console.log('✅ Main categories loaded from /api/admin/categories/main');
            return;
          }
        }
      } catch (mainError) {
        console.log('⚠️ Main categories endpoint failed, trying fallback:', mainError);
      }

      // Fallback: Get all categories and filter main categories
      console.log('🔄 Using fallback: Fetching all categories and filtering main categories');
      const response = await fetch(`${apiUrl}/api/admin/categories`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch categories`);
      }

      const data = await response.json();
      let allCategories = [];
      
      if (data.success && data.data) {
        allCategories = Array.isArray(data.data) ? data.data : [];
      } else if (Array.isArray(data)) {
        allCategories = data;
      } else if (data.data && Array.isArray(data.data)) {
        allCategories = data.data;
      }

      // Filter main categories (level 0 or no parent)
      const mainCats = allCategories.filter(cat => {
        const p = getRowParentId(cat);
        const hasNoParent = !p;
        const isLevel0 = cat.level === 0 || cat.level === '0';
        return hasNoParent || isLevel0;
      });

      setMainCategories(mainCats);
      console.log(`✅ Main categories loaded from fallback: ${mainCats.length} categories`);
      
      if (mainCats.length === 0) {
        console.warn('⚠️ No main categories found. You may need to create a main category first.');
      }
    } catch (error) {
      console.error('❌ Error fetching main categories:', error);
      // Don't set error in form - just log it and show empty list
      // User can still create categories even if main categories fail to load
      setMainCategories([]);
      console.warn('⚠️ Could not load main categories. You can still create a main category by leaving all fields empty.');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch subcategories
  const fetchSubcategories = async (mainCategoryId) => {
    try {
      setLoadingCategories(true);
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      const apiUrl = getApiBaseUrlWithoutPath();
      
      // Try to fetch from subcategories endpoint first
      try {
        const response = await fetch(`${apiUrl}/api/admin/categories/subcategories/${mainCategoryId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setSubcategories(Array.isArray(data.data) ? data.data : []);
            console.log('✅ Subcategories loaded from /api/admin/categories/subcategories');
            return;
          }
        }
      } catch (subError) {
        console.log('⚠️ Subcategories endpoint failed, trying fallback:', subError);
      }

      // Fallback: Get all categories and filter subcategories
      console.log('🔄 Using fallback: Fetching all categories and filtering subcategories');
      const response = await fetch(`${apiUrl}/api/admin/categories`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch categories`);
      }

      const data = await response.json();
      let allCategories = [];
      
      if (data.success && data.data) {
        allCategories = Array.isArray(data.data) ? data.data : [];
      } else if (Array.isArray(data)) {
        allCategories = data;
      } else if (data.data && Array.isArray(data.data)) {
        allCategories = data.data;
      }

      // Filter subcategories (parent matches mainCategoryId, level 1)
      const subCats = allCategories.filter(cat => {
        const parentId = getRowParentId(cat);
        const matchesParent = parentId === mainCategoryId || String(parentId) === String(mainCategoryId);
        const isLevel1 = cat.level === 1 || cat.level === '1';
        return matchesParent || (isLevel1 && matchesParent);
      });

      setSubcategories(subCats);
      console.log(`✅ Subcategories loaded from fallback: ${subCats.length} subcategories`);
    } catch (error) {
      console.error('❌ Error fetching subcategories:', error);
      setSubcategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch subcategories for edit mode (to find main category)
  const fetchSubcategoriesForEdit = async (subcategoryId) => {
    try {
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      const apiUrl = getApiBaseUrlWithoutPath();
      
      // Get subcategory details to find its parent
      const response = await fetch(`${apiUrl}/api/admin/categories/${subcategoryId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const subcategory = data.data.category || data.data;
          const mainCategoryId = getRowParentId(subcategory) || subcategory.parent?._id || subcategory.parent;
          if (mainCategoryId) {
            const mid = String(mainCategoryId);
            setSelectedMainCategory(mid);
            fetchSubcategories(mid);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching subcategory for edit:', error);
    }
  };

  // Fetch children categories
  const fetchChildrenCategories = async (subcategoryId) => {
    try {
      setLoadingCategories(true);
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      const apiUrl = getApiBaseUrlWithoutPath();
      
      // Try to fetch from children categories endpoint first
      try {
        const response = await fetch(`${apiUrl}/api/admin/categories/children/${subcategoryId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setChildrenCategories(Array.isArray(data.data) ? data.data : []);
            console.log('✅ Children categories loaded from /api/admin/categories/children');
            return;
          }
        }
      } catch (childrenError) {
        console.log('⚠️ Children categories endpoint failed, trying fallback:', childrenError);
      }

      // Fallback: Get all categories and filter children categories
      console.log('🔄 Using fallback: Fetching all categories and filtering children categories');
      const response = await fetch(`${apiUrl}/api/admin/categories`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch categories`);
      }

      const data = await response.json();
      let allCategories = [];
      
      if (data.success && data.data) {
        allCategories = Array.isArray(data.data) ? data.data : [];
      } else if (Array.isArray(data)) {
        allCategories = data;
      } else if (data.data && Array.isArray(data.data)) {
        allCategories = data.data;
      }

      // Filter children categories (parent matches subcategoryId, level 2)
      const childrenCats = allCategories.filter(cat => {
        const parentId = getRowParentId(cat);
        const matchesParent = parentId === subcategoryId || String(parentId) === String(subcategoryId);
        const isLevel2 = cat.level === 2 || cat.level === '2';
        return matchesParent || (isLevel2 && matchesParent);
      });

      setChildrenCategories(childrenCats);
      console.log(`✅ Children categories loaded from fallback: ${childrenCats.length} children categories`);
    } catch (error) {
      console.error('❌ Error fetching children categories:', error);
      setChildrenCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    if (category && (categoryDepth === 1 || categoryDepth === 2) && !String(selectedMainCategory || '').trim()) {
      setError('Main category select karein — sub / child categories ke liye parent main zaroori hai.');
      return;
    }
    if (category && categoryDepth === 2 && !String(selectedSubcategory || '').trim()) {
      setError('Subcategory select karein — child category ka parent sub zaroori hai.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const editingId = category ? (category._id ?? category.id) : '';
      let parentId = resolveParentIdForSubmit(
        editingId,
        selectedMainCategory,
        selectedSubcategory,
        selectedChildrenCategory
      );
      if (parentId === '') parentId = null;

      // Generate hierarchical slug
      let hierarchicalSlug = '';
      const categoryName = formData.name.trim();
      const categorySlug = createSlug(categoryName);
      
      if (parentId) {
        // Build hierarchical path by traversing parent chain
        const pathParts = [categorySlug];
        
        // Get all categories for parent lookup
        const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
        const apiUrl = getApiBaseUrlWithoutPath();
        
        try {
          const response = await fetch(`${apiUrl}/api/admin/categories`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${adminToken}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            let allCategories = [];
            
            if (data.success && data.data) {
              allCategories = Array.isArray(data.data) ? data.data : [];
            } else if (Array.isArray(data)) {
              allCategories = data;
            }
            
            // Find parent and build path
            let currentParentId = parentId;
            const visitedIds = new Set();
            
            while (currentParentId && !visitedIds.has(currentParentId)) {
              visitedIds.add(currentParentId);
              
              const parentCategory = allCategories.find(cat => 
                (cat._id || cat.id) === currentParentId || 
                String(cat._id || cat.id) === String(currentParentId)
              );
              
              if (parentCategory) {
                const parentSlug = parentCategory.slug || createSlug(parentCategory.name);
                if (parentSlug) {
                  pathParts.unshift(parentSlug);
                }
                
                // Get parent's parent
                const grandParentId = parentCategory.parentCategory?._id || 
                                     parentCategory.parentCategory || 
                                     parentCategory.parent?._id || 
                                     parentCategory.parent;
                
                if (grandParentId) {
                  currentParentId = grandParentId;
                } else {
                  break;
                }
              } else {
                break;
              }
            }
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch all categories for hierarchical slug generation:', err);
        }
        
        hierarchicalSlug = pathParts.join('/');
      } else {
        // Main category - just use its slug
        hierarchicalSlug = categorySlug;
      }
      
      // Prepare form data
      const submitData = {
        name: categoryName,
        description: formData.description.trim(),
        image: formData.image || '',
        isFeatured: formData.isFeatured,
        isActive: formData.isActive,
        parent: parentId,
        slug: hierarchicalSlug // Send hierarchical slug to backend
      };

      console.log('🔧 CategoryForm: Submitting data with hierarchical slug:', submitData);
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('❌ CategoryForm: Submit error:', error);
      setError(error.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      // Upload to Cloudinary via backend
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      const formData = new FormData();
      formData.append('images', file);

      const apiUrl = getApiBaseUrlWithoutPath();
      const response = await fetch(`${apiUrl}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload image');
      }

      const result = await response.json();
      
      if (result.success && result.data && result.data.uploads && result.data.uploads.length > 0) {
        const uploadedImage = result.data.uploads[0];
        setFormData(prev => ({
          ...prev,
          image: uploadedImage.url
        }));
        setImagePreview(uploadedImage.url);
        console.log('✅ Image uploaded successfully:', uploadedImage.url);
      } else {
        throw new Error(result.message || 'No image uploaded');
      }
    } catch (error) {
      console.error('❌ Image upload error:', error);
      setError(error.message || 'Failed to upload image');
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setUploadingImage(false);
    }
  };


  return (
    <div className="category-form-overlay">
      <div className="category-form">
        <div className="form-header">
          <h3>{category ? 'Edit Category' : 'Add New Category'}</h3>
          <button 
            className="close-btn"
            onClick={onCancel}
            title="Close form"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        {error && (
          <div className="form-error">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{error}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Category Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter category name"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter category description"
              rows="3"
              maxLength={50000}
              disabled={loading}
            />
          </div>

          {/* 3-Level Category Selector */}
          <div className="form-group">
            <label>Category Level *</label>
            
            {/* Main Category Selector */}
            <div className="category-level-selector">
              <label htmlFor="mainCategory">
                Main Category {categoryDepth === 0 ? '(Current - Main Category)' : ''}
              </label>
              {loadingCategories ? (
                <div style={{ padding: '0.75rem', textAlign: 'center', color: '#666' }}>
                  <i className="fas fa-spinner fa-spin"></i> Loading categories...
                </div>
              ) : (
                <>
                  <select
                    id="mainCategory"
                    value={selectedMainCategory || ''}
                    onChange={handleMainCategoryChange}
                    disabled={loading || loadingCategories}
                  >
                    <option value="">
                      {!category
                        ? mainOptions.length === 0
                          ? 'No main categories — leave empty to create a Main Category'
                          : 'Select Main Category (leave empty to create a Main Category)'
                        : categoryDepth === 0
                          ? 'Root main — list se yahi category chunein (re-parent ke liye doosra main chunein)'
                          : 'Select Main Category'}
                    </option>
                    {mainOptions.map((cat) => {
                      const id = String(cat._id ?? cat.id);
                      return (
                        <option key={id} value={id}>
                          {cat.name}
                        </option>
                      );
                    })}
                  </select>
                  <small className="form-help">
                    {!category
                      ? mainOptions.length === 0
                        ? 'No main categories found. Leave empty to create a new Main Category.'
                        : 'Leave empty for a new Main Category, or select a main to add a Subcategory / Child under it.'
                      : categoryDepth === 0
                        ? 'To keep this as a root category, select this same main in the list. To turn it into a subcategory, choose its new parent main (then pick Subcategory / Child as needed).'
                        : `Saved under main: ${category.parent?.name || category.parentCategory?.name || '—'}`}
                  </small>
                </>
              )}
            </div>

            {/* Subcategory Selector - Shows when main category is selected */}
            {selectedMainCategory && (
              <div className="category-level-selector">
                <label htmlFor="subcategory">
                  Subcategory {categoryDepth === 1 ? '(Current - Subcategory)' : categoryDepth === 2 ? '(Parent)' : '(Optional)'}
                </label>
                <select
                  id="subcategory"
                  value={selectedSubcategory || ''}
                  onChange={handleSubcategoryChange}
                  disabled={loading || loadingCategories}
                >
                  <option value="">
                    {categoryDepth === 1
                      ? 'This row is a Subcategory — pick itself or another sub (see help)'
                      : categoryDepth === 2
                        ? 'Select parent Subcategory'
                        : 'Select Subcategory (leave empty to create a Subcategory under this main)'}
                  </option>
                  {subOptions.map((cat) => {
                    const id = String(cat._id ?? cat.id);
                    return (
                      <option key={id} value={id}>
                        {cat.name}
                      </option>
                    );
                  })}
                </select>
                <small className="form-help">
                  {categoryDepth === 1
                    ? 'Keep this subcategory: choose the row with the same name (current). To move it, change Main above then pick the right Subcategory / Child level.'
                    : categoryDepth === 2
                      ? 'Choose which subcategory this child belongs under.'
                      : 'Leave empty to add a new Subcategory under the selected main. Select a sub to add or edit a Child under it.'}
                </small>
              </div>
            )}

            {/* Children Category Selector - Shows when subcategory is selected */}
            {selectedSubcategory && (
              <div className="category-level-selector">
                <label htmlFor="childrenCategory">
                  Children Category {categoryDepth === 2 ? '(Current - Children Category)' : '(Optional)'}
                </label>
                <select
                  id="childrenCategory"
                  value={selectedChildrenCategory || ''}
                  onChange={handleChildrenCategoryChange}
                  disabled={loading || loadingCategories}
                >
                  <option value="">
                    {categoryDepth === 2
                      ? 'This row is a Child category — pick itself to keep, or another child'
                      : 'Select Child (leave empty to create a new Child under this sub)'}
                  </option>
                  {childOptions.map((cat) => {
                    const id = String(cat._id ?? cat.id);
                    return (
                      <option key={id} value={id}>
                        {cat.name}
                      </option>
                    );
                  })}
                </select>
                <small className="form-help">
                  {categoryDepth === 2
                    ? 'Keep this child: select the same name. To move it, change Main / Sub above, then pick this row again if it appears in the list.'
                    : 'Leave empty for a new Child under the selected subcategory.'}
                </small>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="image">Category Image</label>
            <div className="image-upload-section">
              {imagePreview ? (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                  <button
                    type="button"
                    className="btn-remove-image"
                    onClick={() => {
                      setImagePreview(null);
                      setFormData(prev => ({ ...prev, image: '' }));
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    title="Remove image"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <div className="image-upload-placeholder">
                  <i className="fas fa-image"></i>
                  <span>No image uploaded</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={loading || uploadingImage}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="btn-upload-image"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || uploadingImage}
              >
                {uploadingImage ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Uploading...
                  </>
                ) : imagePreview ? (
                  <>
                    <i className="fas fa-sync"></i>
                    Change Image
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload"></i>
                    Upload Image
                  </>
                )}
              </button>
              {formData.image && (
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, image: e.target.value }));
                    setImagePreview(e.target.value);
                  }}
                  placeholder="Or enter image URL"
                  className="image-url-input"
                  disabled={loading}
                />
              )}
            </div>
            <small className="form-help">
              Upload an image file or enter an image URL
            </small>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleChange}
                disabled={loading}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                <i className="fas fa-star"></i>
                Featured Category
              </span>
            </label>
            <small className="form-help">
              Featured categories appear prominently in the frontend
            </small>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                disabled={loading}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                <i className="fas fa-eye"></i>
                Active Category
              </span>
            </label>
            <small className="form-help">
              Inactive categories are hidden from users
            </small>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              onClick={onCancel} 
              className="btn-cancel"
              disabled={loading}
            >
              <i className="fas fa-times"></i>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {category ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className={category ? 'fas fa-save' : 'fas fa-plus'}></i>
                  {category ? 'Update Category' : 'Create Category'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryForm;



